import React from 'react';
import { Player } from '../../types';
import { Lock, CheckCircle } from 'lucide-react';
import { usePlayerLock } from '../../contexts/PlayerLockContext';

interface ScoringLockWrapperProps {
  players: Player[];
  selectedPlayers: Player[];
  onPlayerSelect: (player: Player) => void;
  maxPlayers: number;
  children: React.ReactNode;
}

export function ScoringLockWrapper({
  players,
  selectedPlayers,
  onPlayerSelect,
  maxPlayers,
  children
}: ScoringLockWrapperProps) {
  const { lockedPlayers = [] } = usePlayerLock();

  // Helper function to check if a player has completed their round
  const hasCompletedRound = (player: Player) => {
    if (!player.scores) return false;
    const completedHoles = new Set(player.scores.map(s => s.hole));
    return completedHoles.size === 18;
  };

  // Helper function to check if a player is locked
  const isPlayerLocked = (playerId: string) => {
    return Array.isArray(lockedPlayers) && lockedPlayers.some(p => p.playerId === playerId);
  };

  return (
    <div>
      <div className="space-y-2 mb-6">
        {players.map(player => {
          const isSelected = selectedPlayers.some(p => p.id === player.id);
          const isLocked = isPlayerLocked(player.id);
          const isCompleted = hasCompletedRound(player);
          const isDisabled = isLocked || isCompleted || (!isSelected && selectedPlayers.length >= maxPlayers);

          return (
            <div
              key={player.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                isSelected
                  ? 'border-green-500 bg-green-50'
                  : isDisabled
                  ? 'border-gray-200 bg-gray-50 opacity-60'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              } transition-colors cursor-pointer`}
              onClick={() => !isDisabled && onPlayerSelect(player)}
            >
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {player.name}
                  {isCompleted && (
                    <span className="ml-2 text-sm text-green-600 font-normal">
                      (Round Complete)
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {player.handicap ? `HC: ${player.handicap}` : 'SCR'}
                  {player.flight && ` • ${player.flight} Flight`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isLocked && (
                  <div className="flex items-center text-amber-600 text-sm">
                    <Lock className="w-4 h-4 mr-1" />
                    Scoring in Progress
                  </div>
                )}
                {isCompleted && (
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Complete
                  </div>
                )}
                {!isLocked && !isCompleted && (
                  <div
                    className={`w-5 h-5 rounded-full border-2 ${
                      isSelected
                        ? 'border-green-500 bg-green-500'
                        : isDisabled
                        ? 'border-gray-300'
                        : 'border-gray-300'
                    }`}
                  >
                    {isSelected && (
                      <svg
                        className="w-full h-full text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {children}
    </div>
  );
}